import React, { useState } from "react";
import { createPortal } from "react-dom";
import AddEmployee from "./AddEmployee";
import AddClient from "./AddClient";
import "./AddMembersChecklist.css";
import { useNavigate } from 'react-router-dom';
import fetchBase from "../utils/fetchBase";


// Utility: flag names with odd patterns (e.g., too many words or symbols)
const isSuspicious = (name) => {
    return name.split(" ").length > 3 || /[^a-zA-Z\s]/.test(name);
};

const AddMembersChecklist = ({missingEmployees, missingClients, onClose, onSkip}) => {
    // 🔹 State Hooks
    const navigate = useNavigate();
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [employeeIndex, setEmployeeIndex] = useState(0);
    const [clientIndex, setClientIndex] = useState(0);
    const [addingType, setAddingType] = useState(null); // "employee" or "client"
    const [isProcessing, setIsProcessing] = useState(false);
    const [showGenerateTemplate, setShowGenerateTemplate] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // 🔹 Toggle Employee Checkbox
    const toggleEmployee = (name) => {
        setSelectedEmployees((prev) =>
            prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
        );
    };

    // 🔹 Toggle Client Checkbox
    const toggleClient = (name) => {
        setSelectedClients((prev) =>
            prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
        );
    };

    // 🔹 Unified Checkbox Handler
    // 🔹 Unified Checkbox Handler
        const handleCheckboxChange = (name, type) => {
            if (type === "employee") {
                toggleEmployee(name);
            } else if (type === "client") {
                toggleClient(name);
            }
        };

        // 🔹 Select All Employees
        const handleSelectAllEmployees = () => {
            if (selectedEmployees.length === missingEmployees.length) {
                setSelectedEmployees([]);
            } else {
                setSelectedEmployees([...missingEmployees]);
            }
        };

        // 🔹 Select All Clients
        const handleSelectAllClients = () => {
            if (selectedClients.length === missingClients.length) {
                setSelectedClients([]);
            } else {
                setSelectedClients([...missingClients]);
            }
        };

    // 🔹 Begin Add Flow
    const handleStartAdd = () => {
        if (selectedEmployees.length === 0 && selectedClients.length === 0) return;

        setIsProcessing(true);

        if (selectedClients.length > selectedEmployees.length) {
            setAddingType("client");
        } else {
            setAddingType("employee");
        }
    };

    // 🔹 Lock parent scroll when inline modal is open
        React.useEffect(() => {
            const container = document.querySelector('.payroll-upload-container');
            if (!container) return;
            if (addingType !== null) {
                container.style.overflow = 'hidden';
            } else {
                container.style.overflow = '';
            }
            return () => {
                container.style.overflow = '';
            };
        }, [addingType]);

        // 🔹 Add Employee Success
        const handleEmployeeSuccess = () => {
        const nextIndex = employeeIndex + 1;
        if (nextIndex < selectedEmployees.length) {
            setEmployeeIndex(nextIndex);
        } else {
            if (selectedClients.length > 0) {
                setAddingType("client");
                setClientIndex(0);
            } else {
                resetFlow();
            }
        }
    };

    // 🔹 Add Client Success
    const handleClientSuccess = () => {
        const nextIndex = clientIndex + 1;

        if (nextIndex < selectedClients.length) {
            setClientIndex(nextIndex);
        } else {
            resetFlow();
        }
    };

    // 🔹 Reset and Show Generate Template Button
    const resetFlow = () => {
        setAddingType(null);
        setEmployeeIndex(0);
        setClientIndex(0);
        setIsProcessing(false);
        setShowGenerateTemplate(true);
    };

    const handleGenerateTemplate = async () => {


        try {
            const response = await fetch(`${fetchBase}/api/payroll/generate-template`, {
                method: "POST",
                headers: {
                    'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                }
            });

            const data = await response.json();

            if (response.ok) {

                setShowSuccess(true); // show checkmark animation

                const generatedName = data.filename || ('Payroll ' + new Date().toLocaleDateString());
                const generatedDate = data.date || new Date().toISOString().split('T')[0];

                localStorage.setItem('uploadedPayrollName', generatedName);
                localStorage.setItem('uploadedPayrollDate', generatedDate);

                setTimeout(() => {
                    navigate("/live-payroll");
                    if (onClose) onClose();
                }, 1500);
            } else {
                setShowSuccess(false);
                alert("❌ Failed to generate template.");
            }
        } catch (err) {
            setShowSuccess(false);
            alert("❌ Unexpected error during template generation.");
        }
    };

    // 🔹 Render
    return (
        <div className="checklist-container">
            <h2>Add Missing Members</h2>

            {/* 🟦 Checklist Section */}
            {/* 🟦 Checklist Section (Updated for Grid Layout) */}
            <div className="missing-members-container">
                <div className={`missing-section ${isProcessing ? "disabled" : ""}`}>
                    <h3 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        Missing Employees
                        <input
                            type="checkbox"
                            checked={missingEmployees.length > 0 && selectedEmployees.length === missingEmployees.length}
                            onChange={handleSelectAllEmployees}
                            disabled={isProcessing}
                            title="Select all employees"
                            style={{ transform: "scale(1.2)", accentColor: "#4a90e2", cursor: "pointer" }}
                        />
                    </h3>
                    <div className="scrollable-section">
                        <div className="missing-grid">
                            {missingEmployees.map((name, index) => (
                                <label key={index} className="member-label">
                                    <input
                                        type="checkbox"
                                        checked={selectedEmployees.includes(name)}
                                        onChange={() => handleCheckboxChange(name, "employee")}
                                    />
                                    {name}
                                    {isSuspicious(name) && (
                                        <span className="warning-icon"> ⚠️</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`missing-section ${isProcessing ? "disabled" : ""}`}>
                    <h3 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        Missing Clients
                        <input
                            type="checkbox"
                            checked={missingClients.length > 0 && selectedClients.length === missingClients.length}
                            onChange={handleSelectAllClients}
                            disabled={isProcessing}
                            title="Select all clients"
                            style={{ transform: "scale(1.2)", accentColor: "#4a90e2", cursor: "pointer" }}
                        />
                    </h3>
                    <div className="scrollable-section">
                        <div className="missing-grid">
                            {missingClients.map((name, index) => (
                                <label key={index} className="member-label">
                                    <input
                                        type="checkbox"
                                        checked={selectedClients.includes(name)}
                                        onChange={() => handleCheckboxChange(name, "client")}
                                    />
                                    {name}
                                    {isSuspicious(name) && (
                                        <span className="warning-icon"> ⚠️ </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 🟩 Start Add Button */}
            <div className="checklist-action-buttons">
                <button
                    onClick={handleStartAdd}
                    className="start-add-btn"
                    disabled={isProcessing}
                >
                    Add Selected Members
                </button>

                <button
                    onClick={() => {
                        setSelectedEmployees([]);
                        setSelectedClients([]);
                        setAddingType(null);
                        setEmployeeIndex(0);
                        setClientIndex(0);
                        setIsProcessing(false);
                        setShowGenerateTemplate(true);
                        if (onSkip) onSkip(); // optional callback if parent wants to react
                    }}
                    className="skip-btn"
                    disabled={isProcessing}
                >
                    Skip
                </button>
            </div>

            {/* 🟨 Generate Template Button After Add Flow */}
            {showGenerateTemplate && (
                <div className="generate-template-wrapper">
                    <button
                        onClick={async () => await handleGenerateTemplate()}
                        className="generate-template-btn"
                    >
                    Generate Template
                    </button>
                </div>
            )}

            {/* 🔳 Processing Overlay */}
            {isProcessing && addingType === null && (
                <div className="overlay">
                    <div className="overlay-message">
                        Processing {selectedEmployees.length > selectedClients.length
                        ? `${selectedEmployees.length} employees`
                        : `${selectedClients.length} clients`}...
                    </div>
                </div>
            )}

            {/* 🔻 Add Forms (Dynamic) */}
            {addingType === "employee" && (
                <div className="inline-modal-container">
                    <AddEmployee
                        key={employeeIndex}
                        autofillName={selectedEmployees[employeeIndex]}
                        countRemaining={selectedEmployees.length - employeeIndex}
                        onSuccess={handleEmployeeSuccess}
                    />
                </div>
            )}

            {addingType === "client" && createPortal(
                            <div style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                zIndex: 1001,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                pointerEvents: "none"
                            }}>
                                <div style={{
                                    width: "90%",
                                    maxWidth: "500px",
                                    maxHeight: "85vh",
                                    overflowY: "auto",
                                    borderRadius: "20px",
                                    pointerEvents: "auto"
                                }}>
                                    <AddClient
                                        key={clientIndex}
                                        autofillName={selectedClients[clientIndex]}
                                        countRemaining={selectedClients.length - clientIndex}
                                        onSuccess={handleClientSuccess}
                                    />
                                </div>
                            </div>,
                            document.body
                        )}
            {showSuccess && (
                <div className="success-overlay">
                    <div className="success-box">
                        <div className="success-circle">
                            <div className="success-check"></div>
                        </div>
                        <p className="success-message">Template generated successfully</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddMembersChecklist;